/**
 * Versioned session cache for ticket form reference data.
 * Categories use lean /category/options + /category/version (invalidate on admin CUD).
 */
import CategoriesProvider from '@/resources/CategoriesProvider'
import TicketTypeProvider from '@/resources/TicketType'
import ContactChannleProvider from '@/resources/ContactChannelProvider'
import PlantProvider from '@/resources/PlantProvider'

const CategoriesService = new CategoriesProvider()
const TicketTypeService = new TicketTypeProvider()
const ContactChannelService = new ContactChannleProvider()
const PlantService = new PlantProvider()

const skipLoading = { skipLoading: true }

let cache = null
let inflight = null

let categoryOnlyCache = null
let categoryOnlyInflight = null

export function invalidateTicketRefCache () {
  cache = null
  inflight = null
  categoryOnlyCache = null
  categoryOnlyInflight = null
}

async function fetchTicketRefsBundle (categoryVersion) {
  const [categoriesRes, ticketType, contactChannel, plant] = await Promise.all([
    CategoriesService.findAllOptions(skipLoading),
    TicketTypeService.findAllType(skipLoading),
    ContactChannelService.findAllChannel(skipLoading),
    PlantService.findAllPlant(skipLoading)
  ])
  const versionFromBody = categoriesRes?.additional?.version
  return {
    categoryVersion: String(versionFromBody ?? categoryVersion ?? '0'),
    categories: categoriesRes?.data,
    ticketType: ticketType?.data,
    contactChannel: contactChannel?.data,
    plant: plant?.data?.map((item) => ({ ...item, code: `${item?.code} - ${item?.name}` }))
  }
}

/**
 * Load ticket dropdown refs. Reuses memory cache when category version still matches.
 */
export async function loadTicketRefs () {
  if (inflight) {
    return inflight
  }
  inflight = (async () => {
    try {
      let version = '0'
      try {
        const { data: versionPayload } = await CategoriesService.getVersion(skipLoading)
        version = String(versionPayload?.version ?? '0')
      } catch (error) {
        console.warn('category version check failed, refetching refs:', error?.message || error)
      }
      if (cache && cache.categoryVersion === version && Array.isArray(cache.categories) && cache.categories.length > 0) {
        return cache
      }
      cache = await fetchTicketRefsBundle(version)
      categoryOnlyCache = {
        categoryVersion: cache.categoryVersion,
        categories: cache.categories
      }
      return cache
    } finally {
      inflight = null
    }
  })()
  return inflight
}

/**
 * Lean category options for knowledge / filters (shares version with ticket-ref cache).
 */
export async function loadCategoryOptions () {
  if (categoryOnlyInflight) {
    return categoryOnlyInflight
  }
  categoryOnlyInflight = (async () => {
    try {
      let version = '0'
      try {
        const { data: versionPayload } = await CategoriesService.getVersion(skipLoading)
        version = String(versionPayload?.version ?? '0')
      } catch (error) {
        console.warn('category version check failed, refetching options:', error?.message || error)
      }

      if (cache && cache.categoryVersion === version && Array.isArray(cache.categories)) {
        return cache.categories
      }
      if (
        categoryOnlyCache
        && categoryOnlyCache.categoryVersion === version
        && Array.isArray(categoryOnlyCache.categories)
      ) {
        return categoryOnlyCache.categories
      }

      const { data, additional } = await CategoriesService.findAllOptions(skipLoading)
      const resolvedVersion = String(additional?.version ?? version)
      categoryOnlyCache = {
        categoryVersion: resolvedVersion,
        categories: data
      }
      if (cache) {
        cache = {
          ...cache,
          categoryVersion: resolvedVersion,
          categories: data
        }
      }
      return data
    } finally {
      categoryOnlyInflight = null
    }
  })()
  return categoryOnlyInflight
}

/** Fire-and-forget warm for list/dashboard mount. */
export function prefetchTicketRefs () {
  return loadTicketRefs().catch((error) => {
    console.warn('prefetchTicketRefs failed:', error?.message || error)
  })
}
